import React, { Component, ErrorInfo, ReactNode } from 'react';

import styles from './ErrorBoundary.module.css';

import LoggingService from '@/shared/services/loggingService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

const isDevelopmentEnv = (): boolean => {
  const nodeEnv = typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined;
  if (nodeEnv) return nodeEnv !== 'production';
  return !!import.meta.env.DEV;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo
    });

    LoggingService.error(
      'React Error Caught',
      error,
      { componentStack: errorInfo.componentStack }
    );
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  toggleDetails = (): void => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className={styles.errorBoundary}
          data-testid="error-boundary-container"
          role="alert"
        >
          <div className={styles.errorContainer} data-testid="error-message">
            <h1>Something went wrong</h1>
            <p>
              We're sorry for the inconvenience. The error has been logged and our team will be
              notified.
            </p>

            {isDevelopmentEnv() && (
              <>
                <button
                  onClick={this.toggleDetails}
                  className={styles.detailsButton}
                  type="button"
                >
                  {this.state.showDetails ? 'Hide Details' : 'Show Details'}
                </button>

                {this.state.showDetails && (
                  <div className={styles.errorDetails} data-testid="error-details">
                    <h3>Error Details</h3>
                    <pre data-testid="error-message-detail">
                      {this.state.error?.toString()}
                    </pre>
                    <h3>Component Stack</h3>
                    <pre data-testid="error-stack">
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                )}
              </>
            )}

            <button
              onClick={this.handleReset}
              className={styles.resetButton}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
